import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';

const normalizeAvailableAgents = result => {
  const list = Array.isArray(result?.list)
    ? result.list
    : Array.isArray(result)
    ? result
    : [];

  return list
    .map(agent => ({
      guid: agent.key || agent.guid,
      name: agent.value || agent.name || agent.title || agent.agentName,
    }))
    .filter(agent => agent.guid);
};

const AgentPickerDialog = ({
  open,
  onClose,
  loading,
  getAvailableAgents,
  onSelectAgent,
  hasSelectedAgent,
  title,
  confirmLabel,
}) => {
  const [{ operationSettings }] = useCookies(['operationSettings']);
  const zoneGuid = operationSettings?.defaultZone?.key;

  const [agents, setAgents] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [selectedAgentGuid, setSelectedAgentGuid] = useState('');

  const dialogTitle =
    title ?? (hasSelectedAgent ? 'تغییر ایجنت' : 'انتخاب ایجنت');
  const dialogConfirmLabel =
    confirmLabel ?? (hasSelectedAgent ? 'تغییر' : 'ثبت');

  useEffect(() => {
    if (!open) {
      setAgents([]);
      setSelectedAgentGuid('');
      return;
    }

    let cancelled = false;

    const loadAgents = async () => {
      if (!zoneGuid) return;
      setFetchLoading(true);
      const result = await getAvailableAgents(zoneGuid);
      if (cancelled) return;
      setAgents(normalizeAvailableAgents(result));
      setFetchLoading(false);
    };

    loadAgents();

    return () => {
      cancelled = true;
    };
  }, [open, zoneGuid, getAvailableAgents]);

  const handleConfirm = async () => {
    if (!selectedAgentGuid) return;
    const ok = await onSelectAgent(selectedAgentGuid);
    if (ok) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      sx={{ direction: 'rtl' }}
      PaperProps={{
        sx: {
          borderRadius: '12px',
          border: '1px solid rgba(0,0,0,0.08)',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
        }}
      >
        {dialogTitle}
      </DialogTitle>
      <DialogContent>
        {fetchLoading ? (
          <CircularProgress
            size={28}
            sx={{
              display: 'block',
              mx: 'auto',
              my: 3,
              color: 'brand.blue.primary',
            }}
          />
        ) : agents.length === 0 ? (
          <Box
            sx={{
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: '12px',
              p: 3,
              textAlign: 'center',
              my: 1,
            }}
          >
            <Typography color="text.secondary">
              ایجنتی برای انتخاب موجود نیست.
            </Typography>
          </Box>
        ) : (
          <Stack gap={1.25} py={1}>
            {agents.map(item => {
              const isSelected = selectedAgentGuid === item.guid;

              return (
                <Box
                  key={item.guid}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedAgentGuid(item.guid)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedAgentGuid(item.guid);
                    }
                  }}
                  sx={{
                    'display': 'flex',
                    'alignItems': 'center',
                    'gap': 1.5,
                    'px': 1.5,
                    'py': 1.25,
                    'borderRadius': '12px',
                    'cursor': 'pointer',
                    'border': '1px solid',
                    'borderColor': theme =>
                      isSelected
                        ? theme.palette.brand.blue.primary
                        : 'rgba(0,0,0,0.08)',
                    'backgroundColor': theme =>
                      isSelected
                        ? alpha(theme.palette.brand.blue.primary, 0.08)
                        : '#fff',
                    'boxShadow': isSelected
                      ? '0 1px 2px rgba(0,0,0,0.06)'
                      : 'none',
                    'transition':
                      'border-color 0.15s ease, background-color 0.15s ease',
                    '&:hover': {
                      borderColor: theme =>
                        alpha(theme.palette.brand.blue.primary, 0.45),
                      backgroundColor: theme =>
                        alpha(theme.palette.brand.blue.secondary, 0.06),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      flexShrink: 0,
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isSelected ? '#fff' : 'brand.blue.primary',
                      backgroundColor: theme =>
                        isSelected
                          ? theme.palette.brand.blue.primary
                          : alpha(theme.palette.brand.blue.secondary, 0.12),
                    }}
                  >
                    <PhoneAndroidIcon sx={{ fontSize: 20 }} />
                  </Box>

                  <Typography
                    sx={{
                      flex: 1,
                      fontWeight: 700,
                      fontSize: 14,
                      color: isSelected
                        ? 'brand.blue.primary'
                        : 'brand.grey.primary',
                    }}
                  >
                    {item.name}
                  </Typography>

                  {isSelected ? (
                    <CheckRoundedIcon
                      sx={{ fontSize: 20, color: 'brand.orange.primary' }}
                    />
                  ) : null}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1, p: 3, pt: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          انصراف
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleConfirm}
          loading={loading}
          disabled={!selectedAgentGuid}
        >
          {dialogConfirmLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AgentPickerDialog;
